import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

const initialProfile = {
  bio: '',
  profilePictureUrl: '',
  website: '',
  contactNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  instagramHandle: '',
  twitterHandle: '',
  facebookHandle: '',
  youtubeHandle: '',
  tiktokHandle: '',
};

const CreatorProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(initialProfile);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'creator') return;
    api.get(`/users/${user.userId}/profile/manage`)
      .then(res => setProfile({ ...initialProfile, ...res.data }))
      .catch(() => setProfile(initialProfile))
      .finally(() => setLoading(false));
  }, [user]);

  const handleChange = e => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      await api.put(`/users/${user.userId}/profile/manage`, profile);
      setEditMode(false);
      setMessage('Profile updated!');
    } catch {
      setMessage('Update failed.');
    }
  };

  const handleViewPublicProfile = () => {
    navigate(`/creator/${user.userId}`);
  };

  if (!user) return <div className="p-8 text-center">Please log in to view your profile.</div>;
  if (user.role !== 'creator') return <div className="p-8 text-center">Access denied.</div>;
  if (loading) return <div className="p-8 text-center">Loading profile...</div>;

  return (
    <>
      <Helmet>
        <title>Edit Profile - Creator Dashboard | CollabOrb</title>
        <meta name="description" content="Manage your Creator profile on CollabOrb. Update your bio, social media links, contact information, and showcase your portfolio to attract brand partnerships." />
        <meta name="keywords" content="Creator profile management, edit profile, Creator dashboard, social media links, portfolio management, CollabOrb" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-xl ml-8 mt-10 bg-white p-6 rounded-xl shadow space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          {profile.profilePictureUrl && (
            <img
              src={profile.profilePictureUrl}
              alt="Profile"
              className="h-16 w-16 object-cover rounded-full mr-4"
            />
          )}
          <h2 className="text-2xl font-bold">My Profile</h2>
        </div>
        <button
          onClick={handleViewPublicProfile}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2 text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          View Public Profile
        </button>
      </div>
      {editMode ? (
        <div className="space-y-2 text-left">
          <label className="block font-semibold">Bio</label>
          <textarea
            name="bio"
            value={profile.bio}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="Short bio"
          />
          <label className="block font-semibold">Profile Picture URL</label>
          <input
            name="profilePictureUrl"
            value={profile.profilePictureUrl}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="Profile picture URL"
          />
          <label className="block font-semibold">Website</label>
          <input
            name="website"
            value={profile.website}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="Website"
          />
          <label className="block font-semibold">Contact Number</label>
          <input
            name="contactNumber"
            value={profile.contactNumber}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="Contact Number"
          />
          <label className="block font-semibold">Address Line 1</label>
          <input
            name="addressLine1"
            value={profile.addressLine1}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="Address Line 1"
          />
          <label className="block font-semibold">Address Line 2</label>
          <input
            name="addressLine2"
            value={profile.addressLine2}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="Address Line 2"
          />
          <label className="block font-semibold">City</label>
          <input
            name="city"
            value={profile.city}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="City"
          />
          <label className="block font-semibold">State</label>
          <input
            name="state"
            value={profile.state}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="State"
          />
          <label className="block font-semibold">Postal Code</label>
          <input
            name="postalCode"
            value={profile.postalCode}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="Postal Code"
          />
          <label className="block font-semibold">Country</label>
          <input
            name="country"
            value={profile.country}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="Country"
          />
          <label className="block font-semibold">Instagram Handle</label>
          <input
            name="instagramHandle"
            value={profile.instagramHandle}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="Instagram"
          />
          <label className="block font-semibold">Twitter Handle</label>
          <input
            name="twitterHandle"
            value={profile.twitterHandle}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="Twitter"
          />
          <label className="block font-semibold">Facebook Handle</label>
          <input
            name="facebookHandle"
            value={profile.facebookHandle}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="Facebook"
          />
          <label className="block font-semibold">YouTube Handle</label>
          <input
            name="youtubeHandle"
            value={profile.youtubeHandle}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="YouTube"
          />
          <label className="block font-semibold">TikTok Handle</label>
          <input
            name="tiktokHandle"
            value={profile.tiktokHandle}
            onChange={handleChange}
            className="w-full border rounded p-2 mb-2"
            placeholder="TikTok"
          />
          <div className="flex gap-2 mt-2">
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded"
              onClick={handleSave}
            >
              Save
            </button>
            <button
              className="text-gray-600 underline"
              onClick={() => setEditMode(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 text-left">
          <div><span className="font-semibold">Bio:</span> {profile.bio || '--'}</div>
          <div><span className="font-semibold">Website:</span> {profile.website || '--'}</div>
          <div><span className="font-semibold">Contact Number:</span> {profile.contactNumber || '--'}</div>
          <div><span className="font-semibold">Address:</span> {[
            profile.addressLine1,
            profile.addressLine2,
            profile.city,
            profile.state,
            profile.postalCode,
            profile.country
          ].filter(Boolean).join(', ') || '--'}</div>
          <div><span className="font-semibold">Instagram:</span> {profile.instagramHandle || '--'}</div>
          <div><span className="font-semibold">Twitter:</span> {profile.twitterHandle || '--'}</div>
          <div><span className="font-semibold">Facebook:</span> {profile.facebookHandle || '--'}</div>
          <div><span className="font-semibold">YouTube:</span> {profile.youtubeHandle || '--'}</div>
          <div><span className="font-semibold">TikTok:</span> {profile.tiktokHandle || '--'}</div>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded mt-2"
            onClick={() => setEditMode(true)}
          >
            Edit
          </button>
        </div>
      )}
      {message && <div className="mt-2 text-green-600">{message}</div>}
    </div>
    </>
  );
};

export default CreatorProfilePage;
